import { Platform } from 'react-native';
import SHA256 from 'sha.js';
import dayjs from 'dayjs';
import {
  MarketingMeta,
  RegistrationData,
  SubmitResponse,
} from './registrationForm.types';
import { defaultDateFormat, ymdDateFormat } from './registrationForm.constants';

export const extractMarketingMeta = (
  searchParams: URLSearchParams,
): MarketingMeta => ({
  gclid: searchParams.get('gclid'),
  adfcd: searchParams.get('adfcd'),
  fbclid: searchParams.get('fbclid'),
  msclkid: searchParams.get('msclkid'),
  utm_source: searchParams.get('utm_source'),
  utm_medium: searchParams.get('utm_medium'),
  utm_campaign: searchParams.get('utm_campaign'),
  utm_content: searchParams.get('utm_content'),
  utm_term: searchParams.get('utm_term'),
  utm_variant: searchParams.get('utm_variant'),
  referral_code: searchParams.get('referral_code'),
  btag: searchParams.get('btag'),
  s1: searchParams.get('s1'),
  t1: searchParams.get('t1'),
  lpageid: searchParams.get('lpageid'),
  ad_group: searchParams.get('ad_group'),
  adset: searchParams.get('adset'),
  creative: searchParams.get('creative'),
  keyword: searchParams.get('keyword'),
  network: searchParams.get('network'),
  wbraid: searchParams.get('wbraid'),
  gbraid: searchParams.get('gbraid'),
  ttclid: searchParams.get('ttclid'),
  dclid: searchParams.get('dclid'),
});
export const mapLimits = (limits: RegistrationData['limits']) =>
  Object.keys(limits).reduce(
    (acc, type) => {
      const limitType = type as keyof typeof limits;

      Object.keys(limits[limitType]).forEach((period) => {
        const limitPeriod = period as keyof (typeof limits)[typeof limitType];
        const amount = limits[limitType][limitPeriod];

        if (amount) {
          acc.push({
            type,
            period,
            amount,
          });
        }
      });

      return acc;
    },
    [] as { type: string; period: string; amount: number }[],
  );

export const mapFormData = (data: RegistrationData, codeChallenge: string) => ({
  account: {
    email: data.email,
    password: data.password,
    hasMFAEnabled: false,
    isTestAccount: false,
    personalDetails: {
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      gender: data.gender,
      occupation: data.occupation,
      industry: data.industry,
      addressLine1: `${data.address.unit} ${data.address.buildingNumber} ${data.address.streetName}`,
      city: data.address.city,
      postalCode: data.address.postalCode,
      phoneNumber: data.phone.phoneNumber,
      phoneCode: data.phone.phoneCode,
      birthDate: data.birthDate,
      province: data.address.province,
      provinceCode: data.address.provinceCode,
      country: data.address.country,
      countryCode: data.address.countryCode,
    },
    marketingPreferences: {
      allowEmailMarketingTool: data.marketing,
      allowSMSMarketingTool: data.marketing,
    },
    registrationMeta: {
      gamingLevel: 'Experienced',
      marketingMeta: {
        ...data.marketingMeta,
        promotion_id: data.promoId,
      },
      marketingMetaHistory: data.marketingMetaHistory,
    },
  },
  oAuthParams: {
    clientId: Platform.select({
      ios: 'betty_app_ios',
      android: 'betty_app_android',
      web: 'betty_webrt',
    }),
    scope: 'openid profile betty-api offline_access',
    redirectUri: 'bettyapp://callback',
    codeChallenge,
  },
  limits: mapLimits(data.limits),
});

export const mapRegistrationCompletedEventData = ({
  formData,
  account,
}: SubmitResponse) => ({
  email: SHA256('sha256').update(formData.email).digest('hex'),
  phoneNumber: SHA256('sha256')
    .update(`${formData.phone.phoneCode}${formData.phone.phoneNumber}`)
    .digest('hex'),
  firstName: SHA256('sha256').update(formData.firstName).digest('hex'),
  lastName: SHA256('sha256').update(formData.lastName).digest('hex'),
  dateOfBirth: SHA256('sha256')
    .update(dayjs(formData.birthDate, defaultDateFormat).format(ymdDateFormat))
    .digest('hex'),
  gender: SHA256('sha256').update(formData.gender.slice(0, 1)).digest('hex'),
  city: SHA256('sha256').update(formData.address.city).digest('hex'),
  state: SHA256('sha256').update(formData.address.province).digest('hex'),
  zipCode: SHA256('sha256').update(formData.address.postalCode).digest('hex'),
  country: SHA256('sha256').update(formData.address.country).digest('hex'),
  accountId: SHA256('sha256').update(`${account.accountId}`).digest('hex'),
  advertiserTrackingEnabled: 'True',
  applicationTrackingEnabled: 'True',
  extinfo: [
    'a2',
    '',
    '',
    '',
    '10.1.1',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
  ],
});
